import { format } from 'date-fns';
import { ampli } from '../../ampli';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { useEffect, useState } from "react";
import { Chart } from "react-google-charts";
import { API, ShareAPI } from "../../API";
import { useTranslation } from "react-i18next";
import { LoaderSmall } from "../LoaderSmall/LoaderSmall";
import { WidgetContentModal } from '../WidgetContentModal/WidgetContentModal';
import { AddToNarrativeModal } from '../AddToNarrativeModal/AddToNarrativeModal';
import { AddToSourceGroupModal } from '../AddToSourceGroupModal/AddToSourceGroupModal';

import styles from '../ActorsTypesWidget/ActorsTypes.module.scss';

const attitudeData = {
  objects: [
    { attitude: 'negotiate', count: 18 },
    { attitude: 'cooperate', count: 10 },
    { attitude: 'counteract', count: 14 },
    { attitude: 'attack', count: 3 },
  ],
};

export const AttitudeColumnChart = ({
  report,
  story,
  isShare,
  isClickable = true,
  sourceGroups,
  countries,
  startDate,
  endDate
}) => {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const [attitudes, setAttitudes] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filter, setFilter] = useState(null);
  const [addToCaseModal, setAddToCaseModal] = useState({ isOpen: false, attitude: null });
  const [addToGroupModal, setAddToGroupModal] = useState({ isOpen: false, attitude: null });
  const api = isShare ? ShareAPI : API;

  const attitudeNameMap = {
    negotiate: t('Negotiate'),
    attack: t('Attack'),
    cooperate: t('Pivot'),
    counteract: t('Counteract'),
  };

  const getAttitudes = () => {
    if (!story) return;
    const urlParams = new URLSearchParams();
    urlParams.append('breakdown', 'attitude');
    if (sourceGroups.length > 0) {
      sourceGroups.forEach((sourceGroup) => {
        urlParams.append('source_group_ids', sourceGroup.value);
      });
    }
    if (countries.length > 0) {
      countries.forEach((country) => {
        urlParams.append('source_country_ids', country.value);
      });
    }
    if (startDate) {
      urlParams.append('date_from', format(startDate, 'yyyy-LL-dd'));
    }
    if (endDate) {
      urlParams.append('date_to', format(endDate, 'yyyy-LL-dd'));
    }

    api.fetch('GET', `/API/v1/topic-modeling/${report?.id}/events/${story?.id}/attitudes/breakdown?${urlParams.toString()}`).then(
      data => setAttitudes(data)
    );
  };

  useEffect(() => {
    getAttitudes();
  }, [story]);

  const colors = {
    negotiate: '#B2DDFF',
    attack: '#ffb3b3',
    cooperate: '#FFCC80',
    counteract: '#FFFBD1',
  };
  const stroke_colors = {
    negotiate: '#00368d',
    attack: '#6d0000',
    cooperate: '#D96C00',
    counteract: '#A28F00',
  };

  const getChartData = (attitudes) => {
    let data = [
      ['Attitude', 'Number', { role: "style" }, { role: 'annotation' }]
    ];

    attitudes.objects?.forEach((attitude) =>
      data.push([attitudeNameMap[attitude.attitude], attitude.count, `stroke-color: ${stroke_colors[attitude.attitude]}; color: ${colors[attitude.attitude]}`, attitude.count])
    );

    return data;
  };

  const options = {
    chartArea: { left: '5%', top: '15%', width: '100%', height: '75%' },
    legend: 'none',
    bar: { groupWidth: "55%" },
    annotations: { alwaysOutside: true, stem: { color: 'transparent' }, textStyle: { bold: true, opacity: 1, color: '#151419' }, highContrast: false }
  };

  return (
    <>
      {attitudeData ? (
        attitudeData.objects?.length > 0 ? (
          <div className={styles.chartWrapper}>
            <Chart
              chartType="ColumnChart"
              data={getChartData(attitudeData)}
              width={'100%'}
              height={'400px'}
              className="barChart"
              options={options}
              chartEvents={
                isClickable
                  ? [
                      {
                        eventName: 'select',
                        callback({ chartWrapper }) {
                          // getChart() is null when a click lands mid-redraw (OSAVUL-FRONTEND-G9)
                          let selected = chartWrapper.getChart()?.getSelection();
                          if (!selected?.length) return;
                          let index = selected[0]?.row;
                          let bucket = attitudes.objects?.[index]?.attitude;
                          if (bucket === undefined) return;
                          setFilter(bucket);
                          setIsModalOpen(true);
                          ampli.track({
                            event_type: 'Open widget popup in event',
                            event_properties: {
                              user_id: currentUser?.id,
                              workspace_id: currentUser?.workspace_id,
                              report_id: report?.id,
                              popup_type: 'Attitude Types',
                              popup_value: 'Attitude: ' + attitudeNameMap[bucket],
                            }
                          });
                        },
                      },
                    ]
                  : []
              }
              style={{ margin: '0px', padding: '0px' }}
              chartVersion="51"
            />
          </div>
        ) : (
          <div className={styles.noData}>
            {t('There is no data to display')}
          </div>
        )
      ) : (
        <div className={styles.widgetLoader}>
          <LoaderSmall />
        </div>
      )}

      {isModalOpen ? (
        <WidgetContentModal
          infoModal={isModalOpen}
          isChart={true}
          platformOption={null}
          type={null}
          subType={null}
          stateAffiliatedId={null}
          countryId={countries?.length > 0 ? countries : null}
          audienceRange={null}
          narrative={report}
          sourceGroup={
            sourceGroups.length > 0
              ? sourceGroups?.map((sourceGroup) => sourceGroup.value)
              : null
          }
          source={null}
          sentimentOptions={[filter]}
          eventIdsOptions={[story.id]}
          startDate={startDate}
          endDate={endDate}
          contentTypeOptions={null}
          isShare={isShare}
          onChange={(bool) => {
            setIsModalOpen(bool);
          }}
          showAddToActions={true}
          onAddToCase={(bool) => {
            setAddToCaseModal({
              isOpen: bool,
              attitude: filter,
            });
          }}
          onAddToGroup={(bool) => {
            setAddToGroupModal({
              isOpen: bool,
              attitude: filter,
            });
          }}
        />
      ) : (
        ''
      )}
      {addToCaseModal.isOpen ? (
        <AddToNarrativeModal
          isOpen={addToCaseModal.isOpen}
          deduplication={false}
          caseId={report?.id}
          eventIds={[story?.id]}
          areAllMessagesSelected={true}
          startDate={startDate}
          endDate={endDate}
          sourceGroups={sourceGroups}
          countries={countries}
          attitudes={[addToCaseModal.attitude]}
          onChange={(bool) => {
            setAddToCaseModal({
              isOpen: bool,
              attitude: null,
            });
          }}
        />
      ) : (
        ''
      )}
      {addToGroupModal.isOpen ? (
        <AddToSourceGroupModal
          isOpen={addToGroupModal.isOpen}
          deduplication={false}
          areAllMessagesSelected={true}
          startDate={startDate}
          endDate={endDate}
          sourceGroups={sourceGroups}
          countries={countries}
          attitudes={[addToGroupModal.attitude]}
          narrative={report}
          eventIds={[story?.id]}
          onChange={(bool) => {
            setAddToGroupModal({
              isOpen: bool,
              attitude: null,
            });
          }}
        />
      ) : (
        ''
      )}
    </>
  );
};